/* @@MERGE_ENGINE_START@@ — qa/merge_props.js slices between these two markers and evals
   the result, so the property tests run the SHELL's engine and cannot drift from it. */
/* ══ v14 (W1): THE MERGE ENGINE — union by record key, highest rev wins ═══════════════════
   ⚠ NOTHING CALLS THIS YET, AND THAT IS DELIBERATE — DO NOT SWEEP IT AS DEAD CODE.
   Wave W1's whole shape is that the merge is a PURE FUNCTION, so it can be built and proven
   before the boot rewrite (W4) that will feed it. `qa/merge_props.js` EXTRACTS this block
   between the markers below and exercises it headlessly under Node, so deleting it as
   "unreferenced" would take the entire property battery with it. Same standing as the
   retained TrendAI mark: unused BY DESIGN. See docs/SCOPE.md §v14.

   THE RULE. Nobody shares a file: every writer saves only their own part-file, and the app
   reads all parts and merges them in memory, so OneDrive's fork condition never arises. Per
   record key the copy with the highest REV COUNTER wins; a genuine tie is broken by a hash
   of the record key and the writer id. COUNTERS, NEVER WALL CLOCKS — five machines with five
   wrong clocks change nothing. Deletions are TOMBSTONES (`del: 1`) which stay in the merged
   result, because a tombstone that vanished would let a colleague's older copy resurrect the
   record on the next merge.

   ⚠ THREE FACTS THE W1 RECON ESTABLISHED AGAINST THE REAL DATA. Each would have silently
   corrupted merges if assumed rather than checked:
     1. PROJECTS ARE KEYED BY `uid`, NOT `id`. `id` is a name-derived slug; `uid` is a UUID
        and is present on all 6 live projects and all 3 fixture projects. Two writers naming
        a project alike would produce the same slug, and keying on it would FUSE two
        different projects into one.
     2. EVERY NESTED ID IS A PER-PARENT COUNTER. actions, risks, stakeholders, assumptions,
        workstreams and sessions all restart at "1" in EVERY project, and benefits at "1" in
        every programme — verified duplicated across the live projects. So nested records are
        keyed WITHIN their parent, never globally; that is why this recurses instead of making
        one flat pass. A flat pass would merge project A's action 1 with project B's action 1.
     3. CHANGE REQUESTS ARE KEYED BY `num`, NOT `id`. Keying them by `id` would find no key at
        all and quietly merge nothing.

   ORDERING. Convergence demands that the output order be a function of the SET of records,
   never of the order the parts happened to arrive in, so every merged collection is SORTED by
   its key with a digit-aware compare ("2" before "10"). Consequence, deliberately accepted:
   after a merge, array order is no longer insertion order, so any view that depends on
   insertion order must sort explicitly — to be VERIFIED in W4/W5, not assumed here.

   ABSENT STAYS ABSENT. Most of these collections are LAZY keys. The merge never creates a
   collection that no part had, because materialising one would write `changeRequests: []`
   where nothing stood before and break the lazy-key discipline the byte-exact round-trip
   depends on.

   PURITY. The inputs are never mutated. Untouched substructure is SHARED with the inputs
   (copy-on-write), so callers must not mutate the result in place either. */

/* Every mergeable collection: where it lives relative to its parent record, and what
   identifies a record inside it. ⚠ THIS TABLE AND W2's REV-COUNTER SWEEP MUST NAME THE SAME
   SET — a collection instrumented but not listed here silently never merges, and one listed
   but not instrumented silently loses every collision. The property battery asserts the
   shape of this table so the two cannot drift apart unnoticed. */
const MERGE_SPEC = {
  root:      [{ path: 'programmes', key: 'id',  kids: 'programme' },
              { path: 'projects',   key: 'uid', kids: 'project'   }],
  programme: [{ path: 'benefits',   key: 'id' }],
  project:   [{ path: 'actions',                key: 'id'  },
              { path: 'risks',                  key: 'id'  },
              { path: 'stakeholders',           key: 'id'  },
              { path: 'assumptions',            key: 'id'  },
              { path: 'workstreams',            key: 'id'  },
              { path: 'sessions',               key: 'id'  },
              { path: 'appMeta.changeRequests', key: 'num' },
              { path: 'appMeta.communications', key: 'id'  },
              /* v14.4 (a): continuous-improvement entries — the comms pattern exactly. ⚠ This
                 line is what gives them per-record revs, tombstoned deletes and the merge
                 itself; without it entries would not merge between writers at all. */
              { path: 'appMeta.improvements',   key: 'id'  },
              /* KPIs are an OBJECT keyed by its own property names (cost/risk/schedule/
                 scope/quality), not a list — hence `map`, not `key`. */
              { path: 'appMeta.dashboard.kpis', map: true  }]
};

/* The only top-level keys a part may carry. An unknown one THROWS rather than being dropped:
   silently discarding a colleague's data is the failure this whole design exists to prevent,
   so a new top-level key must be added here deliberately. */
const MERGE_TOP_KEYS = ['schemaVersion', 'savedAt', 'writer', 'programmes', 'projects'];

/* ══ v14 (W7): THE COLLISION SINK — how a supersede note learns it has something to report ═══
   ⚠ WHY IT LIVES INSIDE THE PURE ENGINE. The losing candidate exists ONLY here. By the time
   mergeParts has returned, the merged portfolio holds the winner and nothing anywhere remembers
   what it beat — so recovering it afterwards would mean diffing my part against the result and
   guessing, which is exactly the "afterwards by diffing" §v14 rules out. The merge records what
   it discards, at the moment it discards it.

   ⚠ IT DOES NOT CHANGE THE MERGE'S OUTPUT, and that is what keeps the property battery honest:
   the sink is write-only side information. Commutativity, associativity and idempotence are
   properties of the RETURNED VALUE, which is untouched. Disarmed (null) — which is how every
   property test runs it — the engine behaves exactly as it did in W1.

   ⚠ WHAT COUNTS AS A COLLISION, AND THIS IS THE WHOLE RULE: ONLY A REV TIE. _mergeBeats compares
   rev first, so a winner with a HIGHER rev published after seeing my version — ordinary
   sequential collaboration, and a note there would fire every time a colleague refines anything
   I ever touched. When the revs are EQUAL the counter cannot decide and the hash does, and that
   arbitrary choice is precisely the moment somebody's work is discarded by fiat rather than by
   intent. That is the event the note exists to report, and it is the scope's "fires only on a
   true collision, sequential edits fire nothing" stated in code.

   ⚠ THE RESIDUAL, STATED RATHER THAN HIDDEN. If I never merge between a colleague's two
   publications — offline across both — their rev arrives ahead of mine and the tie is never seen
   on this machine, so no note fires even though my edit was superseded. Telling that apart from a
   genuine sequential edit needs causal history, and §v14 chose counters with NO history
   deliberately (record-level, no vector clocks). The two cases are indistinguishable from the
   data a part-file carries; this is a property of the design, not an oversight in the code. */
let _mergeLossSink = null;
function _mergeArmLossSink(me) { _mergeLossSink = me ? { me: me, losses: [] } : null; }
function _mergeDrainLossSink() {
  const s = _mergeLossSink;
  _mergeLossSink = null;
  return (s && s.losses) || [];
}
/* Record MY candidates that lost a REV TIE to somebody else's. */
function _mergeNoteLosses(group, best, spec, path) {
  const me = _mergeLossSink.me;
  if (best.writer === me) return;          /* I won: nothing of mine was discarded */
  const bestRev = best.rec && best.rec.rev | 0;
  for (let i = 0; i < group.length; i++) {
    const c = group[i];
    if (c === best) continue;
    if (c.writer !== me) continue;                     /* somebody else's loss is not my news */
    /* ⚠⚠ v16.7 W2 (F6): THE REV-TIE REQUIREMENT IS GONE, AND THAT WAS THE WHOLE DEFECT.
       This used to read `if (rev !== bestRev) continue;` and call the difference "sequential" —
       i.e. it assumed a winner with a higher rev had published AFTER SEEING my version. §v16.6 P1
       showed the assumption is false: a higher rev only means MORE EDITS HAPPENED. A colleague
       whose part never reached me can sit at rev 3 while I edit from rev 0 and save rev 1 — mine
       loses, and the old test said nothing, which is §v15's worst class (an edit lost to a
       colleague, SILENTLY). Any loss of MY OWN record is now reported.
       ⚠ THIS DOES NOT FLOOD THE LIST, and the reason is the line above rather than this one:
       `c.writer !== me` already limits this to records carrying MY writer stamp, i.e. ones I
       actually edited. A stale copy of a colleague's record that my part merely CARRIES (because
       _buildSaveDB writes the whole portfolio) keeps THEIR stamp and is skipped, as before.
       ⚠ THE COST, ACCEPTED BY HIM DELIBERATELY: when a colleague genuinely built on my work, my
       version still differs from theirs, so a note fires for a loss that cost me nothing. That is
       one line in a list against losing the work — his ruling, *"cheap half"*. Telling the two
       apart needs each save to record what it was based on, which is the expensive half and is NOT
       built. **Which is exactly why the wording must not accuse — see _supRowHtml.**
       ⚠ A rev HIGHER than the winner's cannot occur here: _mergeBeats compares rev first, so a
       record that outranks the winner would BE the winner. */
    /* ⚠ v16.7 W2 (F7): a lost DELETION is no longer dropped. It has no text to show, which is why
       it used to be skipped — but the consequence a person cares about is that something they
       removed is BACK, and that is worth more than the text would have been. Flagged here and
       given its own sentence in the note; _supNoteFrom must not discard it for having no fields. */
    /* ⚠ A TIE WHOSE TWO VERSIONS SAY THE SAME THING IS NOT A COLLISION, and filtering it here
       rather than in the note builder is not tidiness. Most records in two parts are byte-equal
       and unbumped, so `rev 0 === rev 0` makes EVERY one of them a tie: without this the sink
       would collect one entry per record in the portfolio on every single merge, and the note
       builder would then throw them all away. Found by the property battery, which counted the
       untouched `programmes` record alongside the project under test.
       ⚠ Depends on _revCanonOf from the REV block — hoisted in the shell, and merge_props.js
       evals both blocks together. Extracting this block ALONE would break. */
    if (_revCanonOf(c.rec, spec && spec.kids) === _revCanonOf(best.rec, spec && spec.kids)) continue;
    _mergeLossSink.losses.push({
      path: path || '', coll: (spec && spec.path) || '', kids: (spec && spec.kids) || null,
      rev: bestRev, mine: c.rec, win: best.rec, winWriter: best.writer,
      mineDel: !!(c.rec && c.rec.del)   /* v16.7 W2 (F7) */
    });
  }
}

/* FNV-1a, 32-bit. Tiny, dependency-free, and gives the SAME number in every JS engine —
   the tie-break has to be computed identically on five machines or they would not converge.
   Math.imul keeps the multiply in 32-bit space, which a plain `*` would not. */
function _mergeHash32(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

/* Digit-aware key compare, so "2" sorts before "10" rather than after it. Every nested id in
   this data is a decimal counter, so a plain string sort would order them visibly wrongly. */
function _mergeCmpKey(a, b) {
  const ra = String(a).match(/\d+|\D+/g) || [], rb = String(b).match(/\d+|\D+/g) || [];
  for (let i = 0; i < Math.max(ra.length, rb.length); i++) {
    const x = ra[i], y = rb[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    const nx = /^\d/.test(x), ny = /^\d/.test(y);
    if (nx && ny) { const d = Number(x) - Number(y); if (d) return d < 0 ? -1 : 1; }
    else if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/* A record's writer is carried ON the record once W2 instruments it; the part's writer is the
   fallback for legacy records that have none. ⚠ This is what makes the merge ASSOCIATIVE: an
   already-merged result holds records from several writers, so the tie-break must read the
   record's own origin rather than whichever part it is travelling in. */
function _mergeWriterOf(rec, partWriter) {
  return (rec && typeof rec.writer === 'string' && rec.writer) ? rec.writer : partWriter;
}

/* Does candidate a beat candidate b for this key? rev, then hash, then writer id.
   A STRICT TOTAL ORDER — and that is precisely what makes the merge commutative and
   associative, because taking the maximum of a set under a total order cannot depend on the
   order in which you look at it. The hash spreads wins across the team instead of letting one
   writer win every genuine collision forever (his ruling, 2026-08-03). */
function _mergeBeats(a, b, key) {
  const ra = a.rec && a.rec.rev | 0, rb = b.rec && b.rec.rev | 0;   /* absent rev === 0: lazy key */
  if (ra !== rb) return ra > rb;
  const sa = _mergeHash32(key + '\u0000' + a.writer), sb = _mergeHash32(key + '\u0000' + b.writer);
  if (sa !== sb) return sa > sb;
  return a.writer > b.writer;    /* hash collision: still deterministic, never arbitrary */
}

function _mergeGetPath(obj, path) {
  let cur = obj;
  const segs = path.split('.');
  for (let i = 0; i < segs.length; i++) { if (cur === null || cur === undefined) return undefined; cur = cur[segs[i]]; }
  return cur;
}

/* Copy-on-write down the path so the inputs stay untouched. Two calls that share a prefix
   (appMeta.changeRequests then appMeta.communications) both survive, because the second copies
   the copy the first made rather than the original. */
function _mergeSetPath(obj, path, val) {
  const segs = path.split('.');
  let cur = obj;
  for (let i = 0; i < segs.length - 1; i++) {
    const nxt = cur[segs[i]];
    cur[segs[i]] = (nxt && typeof nxt === 'object' && !Array.isArray(nxt)) ? Object.assign({}, nxt) : {};
    cur = cur[segs[i]];
  }
  cur[segs[segs.length - 1]] = val;
}

/* Merge one collection across every candidate that has it. Returns undefined when NO
   candidate had the key, so an absent lazy collection stays absent.
   ⚠ v14 (W7): `prefix` builds the same record path _revWalk uses (`projects/<uid>/actions/12`),
   so a supersede note, the rev baseline and the merge all name a record identically. It is
   OPTIONAL and defaults to '' — nothing but the note sink reads it, and the merged value does
   not depend on it. */
function _mergeCollection(cands, spec, prefix) {
  let present = false;
  const groups = new Map();
  for (let i = 0; i < cands.length; i++) {
    const coll = _mergeGetPath(cands[i].rec, spec.path);
    if (coll === undefined || coll === null) continue;
    present = true;
    if (spec.map) {
      if (typeof coll !== 'object' || Array.isArray(coll)) continue;
      Object.keys(coll).forEach(function (k) {
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push({ rec: coll[k], writer: _mergeWriterOf(coll[k], cands[i].writer) });
      });
    } else {
      if (!Array.isArray(coll)) continue;
      for (let j = 0; j < coll.length; j++) {
        const rec = coll[j];
        const raw = rec ? rec[spec.key] : undefined;
        /* A record with no key cannot be merged, and skipping it would silently lose data —
           so say so loudly instead. Every collection in MERGE_SPEC was verified to carry its
           key on both real data files before this shipped. */
        if (raw === undefined || raw === null || raw === '')
          throw new Error('mergeParts: a record in ' + spec.path + ' has no ' + spec.key);
        const k = String(raw);
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push({ rec: rec, writer: _mergeWriterOf(rec, cands[i].writer) });
      }
    }
  }
  if (!present) return undefined;
  const base = (prefix || '') + spec.path + '/';
  const keys = Array.from(groups.keys()).sort(_mergeCmpKey);
  if (spec.map) {
    const out = {};
    for (let i = 0; i < keys.length; i++) out[keys[i]] = _mergePick(groups.get(keys[i]), keys[i], spec, base + keys[i]).rec;
    return out;
  }
  const out = [];
  for (let i = 0; i < keys.length; i++) {
    const group = groups.get(keys[i]);
    out.push(_mergeRecord(_mergePick(group, keys[i], spec, base + keys[i]), group, spec.kids, base + keys[i]));
  }
  return out;
}

function _mergePick(group, key, spec, path) {
  let best = group[0];
  for (let i = 1; i < group.length; i++) if (_mergeBeats(group[i], best, key)) best = group[i];
  if (_mergeLossSink) _mergeNoteLosses(group, best, spec, path);
  return best;
}

/* The winning record's own fields are last-write-wins at its own grain; its CHILD collections
   are merged per record across every candidate, whoever won the parent. That is what
   "record-level" means for a project: its name and appMeta scalars follow the winner, while
   its actions and risks each resolve independently. */
function _mergeRecord(win, group, kidsName, path) {
  const out = Object.assign({}, win.rec);
  const kids = kidsName ? (MERGE_SPEC[kidsName] || []) : [];
  for (let i = 0; i < kids.length; i++) {
    const merged = _mergeCollection(group, kids[i], path ? path + '/' : '');
    if (merged !== undefined) _mergeSetPath(out, kids[i].path, merged);
  }
  return out;
}

/* Merge every part-file into one portfolio. Commutative, associative and idempotent. */
function mergeParts(parts) {
  if (!Array.isArray(parts) || !parts.length) throw new Error('mergeParts: need at least one part');
  const writers = parts.map(function (p) { return p && p.writer; });
  writers.forEach(function (w) {
    if (typeof w !== 'string' || !w) throw new Error('mergeParts: every part needs a writer id');
  });
  /* Two parts claiming the same writer would make the tie-break a coin toss, so refuse.
     Per ◆8 a writer is person+machine, which is exactly why one human on two laptops is two
     writers rather than one. */
  if (new Set(writers).size !== writers.length)
    throw new Error('mergeParts: writer ids must be unique across parts');
  for (let i = 0; i < parts.length; i++) {
    const extra = Object.keys(parts[i]).filter(function (k) { return MERGE_TOP_KEYS.indexOf(k) === -1; });
    if (extra.length) throw new Error('mergeParts: unknown top-level key(s) ' + extra.join(',') + ' — add them to MERGE_TOP_KEYS deliberately');
  }
  /* The per-PART schema gate (◆7) refuses an out-of-date part BEFORE the merge runs, so
     reaching here with mixed schemas is a bug in the caller, not a case to paper over. */
  const schemas = Array.from(new Set(parts.map(function (p) { return p.schemaVersion; })));
  if (schemas.length !== 1) throw new Error('mergeParts: mixed schemaVersions ' + schemas.join(','));

  const out = { schemaVersion: schemas[0] };
  /* savedAt is the NEWEST across parts. ISO-8601 compares correctly as a string and MAX is
     order-independent, so this cannot vary with sync order. It also answers W6's open
     question about which date the portfolio shows: the newest part's. */
  const stamps = parts.map(function (p) { return p.savedAt; })
                      .filter(function (s) { return typeof s === 'string' && s; });
  if (stamps.length) out.savedAt = stamps.reduce(function (a, b) { return b > a ? b : a; });

  const cands = parts.map(function (p) { return { rec: p, writer: p.writer }; });
  for (let i = 0; i < MERGE_SPEC.root.length; i++) {
    const merged = _mergeCollection(cands, MERGE_SPEC.root[i], '');
    if (merged !== undefined) out[MERGE_SPEC.root[i].path] = merged;
  }
  return out;
}
/* @@MERGE_ENGINE_END@@ */
